import React, { useState, useEffect } from 'react';
import DashboardLayout from '@/components/DashboardLayout';
import { useAuth } from '@/context/AuthContext';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { SearchableSelect } from '@/components/ui/searchable-select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Checkbox } from '@/components/ui/checkbox';
import { toast } from 'sonner';
import { Plus, RefreshCw, ShoppingCart, Factory, FileText, Truck, Package, AlertTriangle, CheckCircle2, XCircle, Clock, RotateCcw, Pencil, Trash2, X, History } from 'lucide-react';
import { Textarea } from '@/components/ui/textarea';
import { format } from 'date-fns';
import PriceHistoryPopup from '@/components/PriceHistoryPopup';
import { formatCurrency, formatRate } from '@/utils/formatters';
import { DesiredPoDateCell, formatDesiredPoDate } from '@/utils/desiredPoDate';
import DeepLinkBackButton from '@/components/DeepLinkBackButton';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Helper to safely extract error message from API response
const getErrorMessage = (error, defaultMsg = 'An error occurred') => {
  const detail = error.response?.data?.detail;
  if (typeof detail === 'string') return detail;
  if (Array.isArray(detail)) return detail.map(e => e.msg || JSON.stringify(e)).join(', ');
  if (detail && typeof detail === 'object') return detail.msg || JSON.stringify(detail);
  return defaultMsg;
};

const statusColors = {
  pending: 'bg-yellow-100 text-yellow-700 border-yellow-200',
  partially_ordered: 'bg-cyan-100 text-cyan-700 border-cyan-200',
  awaiting_approval: 'bg-orange-100 text-orange-700 border-orange-200',
  po_created: 'bg-blue-100 text-blue-700 border-blue-200',
  wo_created: 'bg-purple-100 text-purple-700 border-purple-200',
  rejected: 'bg-red-100 text-red-700 border-red-200',
  cancelled: 'bg-gray-100 text-gray-500 border-gray-200'
};

const statusLabels = {
  pending: 'Pending',
  partially_ordered: 'Partially Ordered',
  awaiting_approval: 'Awaiting PO Approval',
  po_created: 'PO Created',
  wo_created: 'WO Created',
  rejected: 'Rejected',
  cancelled: 'Cancelled'
};

const sourceIcons = {
  MP: <Truck className="w-4 h-4 text-blue-600" />,
  IP: <Factory className="w-4 h-4 text-purple-600" />
};

export default function IndentRegisterPage() {
  const { user, getAuthHeader } = useAuth();
  const [activeTab, setActiveTab] = useState('pending');
  const [indents, setIndents] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [selectedIndents, setSelectedIndents] = useState({});
  const [openPO, setOpenPO] = useState(false);
  const [poForm, setPoForm] = useState({
    supplier_id: '',
    po_date: format(new Date(), 'yyyy-MM-dd'),
    expected_delivery_date: '',
    shipping_unit: 'Assembly Unit',
    notes: '',
    // Line items with pricing
    lineItems: [], // { indent_id, quantity, unit, unit_price, amount }
    // Totals
    other_charges_taxable: 0,
    gst_rate: 18,
    other_charges_non_taxable: 0,
    // Advance payment request
    advance_requested: false,
    advance_amount: '',
    advance_payment_due_date: ''
  });

  const [unitAddresses, setUnitAddresses] = useState([]);
  const [locations, setLocations] = useState([]);  // For location-based filtering
  const [uomOptions, setUomOptions] = useState([]);
  const [unitFilter, setUnitFilter] = useState('all'); // Location filter for indents
  const [itemSearch, setItemSearch] = useState(''); // Item name search
  
  // Edit indent state
  const [editingIndent, setEditingIndent] = useState(null);
  const [editForm, setEditForm] = useState({
    quantity_required: 0,
    unit: '',
    remarks: '',
    sales_order_id: '',
    so_number: ''
  });
  const [editSalesOrders, setEditSalesOrders] = useState([]);

  // Price history popup state
  const [priceHistoryOpen, setPriceHistoryOpen] = useState(false);
  const [priceHistoryItem, setPriceHistoryItem] = useState(null);
  const [items, setItems] = useState([]); // For location fallback lookup

  // Helper: Map shipping unit name to location_id
  const getLocationIdFromShippingUnit = (shippingUnit) => {
    // Map unit names to location codes
    const unitToLocationCode = {
      'Assembly Unit': 'ASSEMBLY',
      'Wood Work Unit': 'WOOD',
      'Metal Work Unit': 'METAL'
    };
    const locationCode = unitToLocationCode[shippingUnit];
    if (!locationCode) return null;
    const location = locations.find(l => l.code === locationCode);
    return location?.id || null;
  };

  // Helper: Get location display - first from indent.location_id, fallback to item's default_location_id
  const getLocationDisplay = (indent) => {
    // First try indent's stored location_id
    let locationId = indent.location_id;
    
    // Fallback to item's current default_location_id
    if (!locationId && indent.item_id) {
      const item = items.find(i => i.id === indent.item_id);
      locationId = item?.default_location_id;
    }
    
    if (!locationId) return null;
    const location = locations.find(l => l.id === locationId);
    return location?.code || location?.name || null;
  };

  const fetchData = async () => {
    try {
      const [indentsRes, suppliersRes, unitsRes, locationsRes, uomRes, itemsRes] = await Promise.all([
        axios.get(`${API_URL}/indents`, { headers: getAuthHeader() }),
        axios.get(`${API_URL}/suppliers`, { headers: getAuthHeader() }),
        axios.get(`${API_URL}/unit-addresses`, { headers: getAuthHeader() }),
        axios.get(`${API_URL}/locations`, { headers: getAuthHeader() }),
        axios.get(`${API_URL}/uom/options`, { headers: getAuthHeader() }),
        axios.get(`${API_URL}/items`, { headers: getAuthHeader() })
      ]);
      setIndents(indentsRes.data);
      setSuppliers(suppliersRes.data);
      setUnitAddresses(unitsRes.data);
      setLocations(locationsRes.data.filter(l => l.is_active !== false));
      setUomOptions(uomRes.data.codes || []);
      setItems(itemsRes.data || []);
    } catch (error) {
      console.error('Fetch error:', error);
      toast.error('Failed to fetch data');
      // Set fallback UOM options if API fails
      if (!uomOptions.length) {
        setUomOptions(['NOS', 'KG', 'MTR', 'SQFT', 'LTR', 'SET']);
      }
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  // Note: "Generate Indents" feature removed - indent creation now handled through Purchase Fulfillment page
  // where quantities can be reviewed before creating indents

  const handleSupplierChange = async (indentId, supplierId) => {
    try {
      const supplier = suppliers.find(s => s.id === supplierId);
      // Use dedicated assign-supplier endpoint (Purchase team can use this)
      await axios.patch(`${API_URL}/indents/${indentId}/assign-supplier`, null, { 
        headers: getAuthHeader(),
        params: {
          supplier_id: supplierId,
          supplier_name: supplier?.name || ''
        }
      });
      
      // Update local state
      setIndents(prev => prev.map(ind => {
        if (ind.id === indentId) {
          return {
            ...ind,
            selected_supplier_id: supplierId,
            selected_supplier_name: supplier?.name
          };
        }
        return ind;
      }));
      toast.success('Supplier assigned');
    } catch (error) {
      toast.error(getErrorMessage(error, 'Failed to assign supplier'));
    }
  };

  const toggleIndentSelection = (indentId) => {
    setSelectedIndents(prev => {
      if (prev[indentId]) {
        // Deselecting - just remove this indent
        const { [indentId]: _, ...rest } = prev;
        return rest;
      }
      // Selecting - add this indent
      return { ...prev, [indentId]: true };
    });
  };

  // Determine if an indent's checkbox should be disabled based on supplier matching
  const isIndentCheckboxDisabled = (indent) => {
    // If nothing is selected, all pending/partially_ordered indents are selectable
    const selectedIds = Object.keys(selectedIndents);
    if (selectedIds.length === 0) return false;
    
    // If this indent is already selected, it should not be disabled (user can deselect it)
    if (selectedIndents[indent.id]) return false;
    
    // Get the supplier of the first selected indent
    const firstSelectedIndent = indents.find(ind => selectedIndents[ind.id]);
    if (!firstSelectedIndent) return false;
    
    const selectedSupplierId = firstSelectedIndent.selected_supplier_id;
    
    // Disable if this indent has a different supplier (or no supplier when one is selected)
    // Allow selection only if suppliers match (both must have the same supplier ID)
    return indent.selected_supplier_id !== selectedSupplierId;
  };

  // Initialize line items when opening PO dialog
  const initializePOLineItems = () => {
    const selectedIndentIds = Object.keys(selectedIndents);
    const selectedIndentList = indents.filter(ind => selectedIndentIds.includes(ind.id));
    
    const lineItems = selectedIndentList.map(ind => {
      // Calculate remaining quantity available for this indent
      // Available = required - ordered (approved) - pending (awaiting approval)
      const availableQty = ind.status === 'partially_ordered' 
        ? (ind.quantity_required || 0) - (ind.quantity_ordered || 0) - (ind.quantity_pending || 0)
        : (ind.quantity_required || 0) - (ind.quantity_ordered || 0) - (ind.quantity_pending || 0);
      
      return {
        indent_id: ind.id,
        item_id: ind.item_id,
        item_name: ind.item_name,
        // Reference fields (read-only)
        indent_quantity: ind.quantity_required,  // Full indent qty (reference only)
        indent_unit: ind.unit,                   // System UOM from Item Master (read-only)
        available_qty: availableQty,             // How much is still available to order
        so_numbers: ind.so_numbers || (ind.so_number ? [ind.so_number] : []),  // SO linkage
        desired_po_date: ind.desired_po_date,    // Read-only context for PM during PO build
        // PO qty - what user wants to order in THIS PO (in system UOM)
        po_qty: availableQty,                    // Default to available qty, user can reduce
        // Supplier fields (editable)
        quantity: availableQty,                  // Supplier qty (can differ due to UOM conversion)
        unit: ind.unit,                          // Supplier UOM (editable)
        unit_price: 0,
        amount: 0
      };
    });
    
    // Get supplier from first indent
    const supplierId = selectedIndentList[0]?.selected_supplier_id || '';
    
    setPoForm(prev => ({
      ...prev,
      supplier_id: supplierId,
      lineItems,
      other_charges_taxable: 0,
      gst_rate: 18,
      other_charges_non_taxable: 0
    }));
  };

  // Update line item field
  const updateLineItem = (index, field, value) => {
    setPoForm(prev => {
      const newLineItems = [...prev.lineItems];
      newLineItems[index] = { ...newLineItems[index], [field]: value };
      
      // Auto-calculate amount
      if (field === 'quantity' || field === 'unit_price') {
        const qty = field === 'quantity' ? parseFloat(value) || 0 : parseFloat(newLineItems[index].quantity) || 0;
        const price = field === 'unit_price' ? parseFloat(value) || 0 : parseFloat(newLineItems[index].unit_price) || 0;
        newLineItems[index].amount = qty * price;
      }
      
      return { ...prev, lineItems: newLineItems };
    });
  };

  // Calculate totals
  const calculateTotals = () => {
    const subTotal = poForm.lineItems.reduce((sum, item) => sum + (parseFloat(item.amount) || 0), 0);
    const taxableAmount = subTotal + (parseFloat(poForm.other_charges_taxable) || 0);
    const gstAmount = taxableAmount * (parseFloat(poForm.gst_rate) || 0) / 100;
    const grandTotal = subTotal + (parseFloat(poForm.other_charges_taxable) || 0) + gstAmount + (parseFloat(poForm.other_charges_non_taxable) || 0);
    
    return { subTotal, gstAmount, grandTotal };
  };

  const handleCreatePO = async () => {
    const selectedIndentIds = Object.keys(selectedIndents);
    
    if (selectedIndentIds.length === 0) {
      toast.error('Please select at least one indent');
      return;
    }

    if (!poForm.supplier_id) {
      toast.error('Please select a supplier');
      return;
    }

    // Auto-derive location_id from shipping_unit
    const locationId = getLocationIdFromShippingUnit(poForm.shipping_unit);
    if (!locationId) {
      toast.error('Unable to determine location from shipping unit. Please check unit configuration.');
      return;
    }

    // Validate line items have pricing and PO qty
    const invalidItems = poForm.lineItems.filter(item => !item.quantity || item.quantity <= 0 || item.unit_price < 0);
    if (invalidItems.length > 0) {
      toast.error('Please enter valid quantity and price for all items');
      return;
    }

    // Validate PO qty doesn't exceed available qty
    const overOrderedItems = poForm.lineItems.filter(item => (item.po_qty || 0) > (item.available_qty || item.indent_quantity));
    if (overOrderedItems.length > 0) {
      toast.error('PO quantity cannot exceed available quantity');
      return;
    }

    try {
      const payload = {
        supplier_id: poForm.supplier_id,
        line_items: poForm.lineItems.map(item => ({
          indent_id: item.indent_id,
          quantity: parseFloat(item.quantity) || 0,           // Supplier qty (in supplier UOM)
          unit: item.unit,                                     // Supplier UOM
          indent_quantity: item.indent_quantity,               // Full indent qty (reference)
          system_qty_ordered: parseFloat(item.po_qty) || item.available_qty || item.indent_quantity,   // Actual PO qty being ordered (in system UOM)
          indent_unit: item.indent_unit,                       // System UOM (from Item Master)
          unit_price: parseFloat(item.unit_price) || 0,
          supplier_item_name: item.supplier_item_name || null
        })),
        po_date: new Date(poForm.po_date).toISOString(),
        expected_delivery_date: poForm.expected_delivery_date ? new Date(poForm.expected_delivery_date).toISOString() : null,
        shipping_unit: poForm.shipping_unit,
        location_id: locationId,
        other_charges_taxable: parseFloat(poForm.other_charges_taxable) || 0,
        gst_rate: parseFloat(poForm.gst_rate) || 0,
        other_charges_non_taxable: parseFloat(poForm.other_charges_non_taxable) || 0,
        notes: poForm.notes || null,
        // Advance payment request
        advance_requested: poForm.advance_requested,
        advance_amount: poForm.advance_requested ? parseFloat(poForm.advance_amount) || null : null,
        advance_payment_due_date: poForm.advance_requested ? poForm.advance_payment_due_date || null : null
      };

      const response = await axios.post(`${API_URL}/purchase-orders`, payload, { headers: getAuthHeader() });
      let successMsg = `PO ${response.data.po_number} sent for approval`;
      if (poForm.advance_requested && poForm.advance_amount) {
        successMsg += ` with advance request of ${formatCurrency(parseFloat(poForm.advance_amount))}`;
      }
      toast.success(successMsg);
      setOpenPO(false);
      setSelectedIndents({});
      setPoForm({
        supplier_id: '',
        po_date: format(new Date(), 'yyyy-MM-dd'),
        expected_delivery_date: '',
        shipping_unit: 'Assembly Unit',
        notes: '',
        lineItems: [],
        other_charges_taxable: 0,
        gst_rate: 18,
        other_charges_non_taxable: 0,
        advance_requested: false,
        advance_amount: '',
        advance_payment_due_date: ''
      });
      fetchData();
    } catch (error) {
      toast.error(getErrorMessage(error, 'Failed to create PO'));
    }
  };

  // Note: handleCreateWO function removed - indents should only go for purchase, not WO creation
  // WOs should be created from Production Fulfillment Register

  const handleDeleteIndent = async (indentId) => {
    if (!confirm('Are you sure you want to delete this indent? This action cannot be undone.')) return;
    
    try {
      await axios.delete(`${API_URL}/indents/${indentId}`, { headers: getAuthHeader() });
      toast.success('Indent deleted successfully');
      fetchData();
    } catch (error) {
      toast.error(getErrorMessage(error, 'Failed to delete indent'));
    }
  };

  const openEditIndent = (indent) => {
    setEditingIndent(indent);
    setEditForm({
      quantity_required: indent.quantity_required || 0,
      unit: indent.unit || 'NOS',
      remarks: indent.remarks || indent.reason || '',
      sales_order_id: indent.sales_order_id || '',
      so_number: indent.so_number || ''
    });
    // Fetch active SOs for the dropdown
    axios.get(`${API_URL}/sales-orders?status=confirmed,in_production`, { headers: getAuthHeader() })
      .then(res => {
        const activeSOs = (res.data || []).filter(so => !['cancelled', 'dispatched', 'completed'].includes(so.status));
        setEditSalesOrders(activeSOs);
      })
      .catch(() => setEditSalesOrders([]));
  };

  const handleUpdateIndent = async () => {
    if (!editingIndent) return;
    
    try {
      const payload = {
        quantity_required: parseFloat(editForm.quantity_required) || 0,
        unit: editForm.unit,
        remarks: editForm.remarks
      };
      // Include SO link fields (send null to clear)
      payload.sales_order_id = editForm.sales_order_id || null;
      payload.so_number = editForm.so_number || null;

      await axios.patch(`${API_URL}/indents/${editingIndent.id}`, payload, { headers: getAuthHeader() });
      
      toast.success('Indent updated successfully');
      setEditingIndent(null);
      fetchData();
    } catch (error) {
      toast.error(getErrorMessage(error, 'Failed to update indent'));
    }
  };

  // Filter indents by status and location
  // Include partially_ordered so users can create additional POs for remaining quantity
  const baseFilteredIndents = indents.filter(ind => {
    // Location filter (filter by location_id from item)
    if (unitFilter !== 'all' && ind.location_id !== unitFilter) {
      return false;
    }
    // Item name search
    if (itemSearch.trim()) {
      const searchLower = itemSearch.toLowerCase();
      if (!(ind.item_name || '').toLowerCase().includes(searchLower)) {
        return false;
      }
    }
    return true;
  });
  
  // Pending tab ordering: desired_po_date ASC (urgent first), nulls last,
  // tie-break by created_at DESC. Awaiting / Processed tabs keep their
  // current order untouched.
  const pendingIndents = baseFilteredIndents
    .filter(ind =>
      ind.status === 'pending' ||
      ind.status === 'rejected' ||
      ind.status === 'partially_ordered'
    )
    .slice()
    .sort((a, b) => {
      const da = a.desired_po_date || '';
      const db = b.desired_po_date || '';
      if (da && db) {
        if (da !== db) return da < db ? -1 : 1;
      } else if (da) {
        return -1; // a has date, b does not — a first
      } else if (db) {
        return 1;  // b has date, a does not — b first
      }
      const ca = a.created_at || '';
      const cb = b.created_at || '';
      if (ca === cb) return 0;
      return ca < cb ? 1 : -1; // created_at DESC tie-break
    });
  const awaitingApprovalIndents = baseFilteredIndents.filter(ind => ind.status === 'awaiting_approval');
  const processedIndents = baseFilteredIndents.filter(ind => ['po_created', 'wo_created'].includes(ind.status));
  
  // Split pending by demand source type (not item's MP/IP)
  const soBuyIndents = pendingIndents.filter(ind => ind.source_type === 'so_buy');
  const woMaterialIndents = pendingIndents.filter(ind => ind.source_type === 'wo_material');
  const reorderIndents = pendingIndents.filter(ind => ind.source_type === 'reorder');
  const manualIndents = pendingIndents.filter(ind => ind.source_type === 'manual' || ind.source_type === 'MP' || !ind.source_type);

  // Get selected indents' supplier (pending and partially_ordered can be selected for PO)
  const selectablePendingIndents = pendingIndents.filter(ind => 
    ind.status === 'pending' || ind.status === 'partially_ordered'
  );
  const selectedIndentList = selectablePendingIndents.filter(ind => selectedIndents[ind.id]);
  const commonSupplierId = selectedIndentList.length > 0 ? selectedIndentList[0].selected_supplier_id : null;
  // NOTE: source_type check removed - all valid indents (regardless of source_type) can be sent for PO
  // The only requirements are: same supplier selected for all, and status is pending/partially_ordered
  const canCreatePO = selectedIndentList.length > 0 && 
    selectedIndentList.every(ind => ind.selected_supplier_id === commonSupplierId && commonSupplierId);

  // Resubmit rejected indent
  const handleResubmitIndent = async (indentId) => {
    try {
      await axios.post(`${API_URL}/indents/${indentId}/resubmit`, {}, { headers: getAuthHeader() });
      toast.success('Indent resubmitted for PO creation');
      fetchData();
    } catch (error) {
      toast.error(getErrorMessage(error, 'Failed to resubmit indent'));
    }
  };

  if (!user) {
    return (
      <DashboardLayout>
        <div className="text-center py-12">
          <p className="text-muted-foreground">You don&apos;t have permission to access this page</p>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="space-y-6">
        <DeepLinkBackButton />
        {/* Header */}
        <div className="flex justify-between items-center">
          <div>
            <h1 className="text-2xl font-bold">Indent Register</h1>
            <p className="text-muted-foreground">Manage purchase requisitions and create Purchase Orders</p>
          </div>
          <div className="flex gap-2 items-center">
            {/* Item Name Search */}
            <Input
              placeholder="Search by item name..."
              value={itemSearch}
              onChange={(e) => setItemSearch(e.target.value)}
              className="w-48"
            />
            {/* Location Filter */}
            <Select value={unitFilter} onValueChange={setUnitFilter}>
              <SelectTrigger className="w-[180px]" data-testid="location-filter">
                <SelectValue placeholder="All Locations" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Locations</SelectItem>
                {locations.map(loc => (
                  <SelectItem key={loc.id} value={loc.id}>
                    {loc.code} - {loc.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
          <Card>
            <CardContent className="pt-4 pb-3">
              <div className="flex items-center gap-2">
                <div className="p-1.5 bg-yellow-100 rounded-lg">
                  <AlertTriangle className="w-4 h-4 text-yellow-600" />
                </div>
                <div>
                  <p className="text-xl font-bold">{pendingIndents.length}</p>
                  <p className="text-xs text-muted-foreground">Total Pending</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-4 pb-3">
              <div className="flex items-center gap-2">
                <div className="p-1.5 bg-blue-100 rounded-lg">
                  <ShoppingCart className="w-4 h-4 text-blue-600" />
                </div>
                <div>
                  <p className="text-xl font-bold">{soBuyIndents.length}</p>
                  <p className="text-xs text-muted-foreground">SO Buy</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-4 pb-3">
              <div className="flex items-center gap-2">
                <div className="p-1.5 bg-purple-100 rounded-lg">
                  <Factory className="w-4 h-4 text-purple-600" />
                </div>
                <div>
                  <p className="text-xl font-bold">{woMaterialIndents.length}</p>
                  <p className="text-xs text-muted-foreground">WO Shortage</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-4 pb-3">
              <div className="flex items-center gap-2">
                <div className="p-1.5 bg-orange-100 rounded-lg">
                  <RefreshCw className="w-4 h-4 text-orange-600" />
                </div>
                <div>
                  <p className="text-xl font-bold">{reorderIndents.length}</p>
                  <p className="text-xs text-muted-foreground">Reorder</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-4 pb-3">
              <div className="flex items-center gap-2">
                <div className="p-1.5 bg-slate-100 rounded-lg">
                  <FileText className="w-4 h-4 text-slate-600" />
                </div>
                <div>
                  <p className="text-xl font-bold">{manualIndents.length}</p>
                  <p className="text-xs text-muted-foreground">Manual</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-4 pb-3">
              <div className="flex items-center gap-2">
                <div className="p-1.5 bg-green-100 rounded-lg">
                  <CheckCircle2 className="w-4 h-4 text-green-600" />
                </div>
                <div>
                  <p className="text-xl font-bold">{processedIndents.length}</p>
                  <p className="text-xs text-muted-foreground">Processed</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList>
            <TabsTrigger value="pending">
              <AlertTriangle className="w-4 h-4 mr-2" />
              Pending ({pendingIndents.length})
            </TabsTrigger>
            <TabsTrigger value="awaiting">
              <Clock className="w-4 h-4 mr-2" />
              Awaiting PO Approval ({awaitingApprovalIndents.length})
            </TabsTrigger>
            <TabsTrigger value="processed">
              <CheckCircle2 className="w-4 h-4 mr-2" />
              Processed ({processedIndents.length})
            </TabsTrigger>
          </TabsList>

          {/* Pending Indents Tab */}
          <TabsContent value="pending">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center justify-between">
                  <span className="flex items-center gap-2">
                    <FileText className="w-5 h-5" />
                    Pending Indents
                  </span>
                  {Object.keys(selectedIndents).length > 0 && (
                    <Badge variant="secondary">
                      {Object.keys(selectedIndents).length} selected
                    </Badge>
                  )}
                </CardTitle>
              </CardHeader>
              <CardContent>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead className="w-12">
                        <Checkbox
                          checked={pendingIndents.filter(ind => ind.status === 'pending').length > 0 && pendingIndents.filter(ind => ind.status === 'pending').every(ind => selectedIndents[ind.id])}
                          onCheckedChange={(checked) => {
                            if (checked) {
                              const newSelected = {};
                              pendingIndents.forEach(ind => { newSelected[ind.id] = true; });
                              setSelectedIndents(newSelected);
                            } else {
                              setSelectedIndents({});
                            }
                          }}
                        />
                      </TableHead>
                      <TableHead>Indent #</TableHead>
                      <TableHead>Linked WO</TableHead>
                      <TableHead>Linked SO(s)</TableHead>
                      <TableHead>Item</TableHead>
                      <TableHead className="text-center">Location</TableHead>
                      <TableHead className="text-center">Source</TableHead>
                      <TableHead className="text-center">Need</TableHead>
                      <TableHead className="text-center">Desired PO Date</TableHead>
                      <TableHead className="text-right">Qty Required</TableHead>
                      <TableHead>Created By</TableHead>
                      <TableHead>Supplier</TableHead>
                      <TableHead className="text-right">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {pendingIndents.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={13} className="text-center py-12">
                          <Package className="w-12 h-12 mx-auto mb-4 text-muted-foreground opacity-50" />
                          <p className="text-muted-foreground">No pending indents</p>
                          <p className="text-sm text-muted-foreground mt-1">Create indents from Purchase Fulfillment or Create Indent page</p>
                        </TableCell>
                      </TableRow>
                    ) : (
                      pendingIndents.map((indent) => (
                        <TableRow key={indent.id} className={`${selectedIndents[indent.id] ? 'bg-primary/5' : ''} ${indent.status === 'rejected' ? 'bg-red-50' : ''}`}>
                          <TableCell>
                            {/* Checkbox available for all pending/partially_ordered indents regardless of source_type */}
                            {/* But disabled if a different supplier is already selected */}
                            {(indent.status === 'pending' || indent.status === 'partially_ordered') && (
                              <Checkbox
                                checked={!!selectedIndents[indent.id]}
                                onCheckedChange={() => toggleIndentSelection(indent.id)}
                                disabled={isIndentCheckboxDisabled(indent)}
                                title={isIndentCheckboxDisabled(indent) ? 'Cannot select: Different supplier than already selected indent(s)' : ''}
                              />
                            )}
                          </TableCell>
                          <TableCell>
                            <Badge variant="outline" className="font-mono">{indent.indent_number}</Badge>
                          </TableCell>
                          <TableCell>
                            {indent.triggered_by_wo_number ? (
                              <Badge variant="secondary" className="font-mono text-xs">{indent.triggered_by_wo_number}</Badge>
                            ) : (
                              <span className="text-muted-foreground text-sm">-</span>
                            )}
                          </TableCell>
                          <TableCell>
                            {/* Display SO linkage - support both so_numbers array and legacy so_number */}
                            {(indent.so_numbers && indent.so_numbers.length > 0) ? (
                              <div className="flex flex-wrap gap-1">
                                {indent.so_numbers.slice(0, 3).map((so, i) => (
                                  <Badge key={i} variant="outline" className="text-xs">{so}</Badge>
                                ))}
                                {indent.so_numbers.length > 3 && (
                                  <Badge variant="outline" className="text-xs">+{indent.so_numbers.length - 3}</Badge>
                                )}
                              </div>
                            ) : indent.so_number ? (
                              <Badge variant="outline" className="text-xs">{indent.so_number}</Badge>
                            ) : (
                              <span className="text-muted-foreground text-xs">-</span>
                            )}
                          </TableCell>
                          <TableCell>
                            <p className="font-medium">{indent.item_name}</p>
                          </TableCell>
                          <TableCell className="text-center">
                            {getLocationDisplay(indent) ? (
                              <Badge variant="outline" className="text-xs">{getLocationDisplay(indent)}</Badge>
                            ) : (
                              <span className="text-muted-foreground text-xs">-</span>
                            )}
                          </TableCell>
                          <TableCell className="text-center">
                            <div className="flex items-center justify-center gap-1">
                              {sourceIcons[indent.source_type]}
                              <span className="text-sm">{indent.source_type}</span>
                            </div>
                          </TableCell>
                          <TableCell className="text-center">
                            <Badge variant={indent.need_type === 'APO' ? 'default' : 'secondary'}>
                              {indent.need_type}
                            </Badge>
                          </TableCell>
                          <TableCell className="text-center">
                            <DesiredPoDateCell value={indent.desired_po_date} />
                          </TableCell>
                          <TableCell className="text-right font-medium">
                            {indent.status === 'partially_ordered' ? (
                              <div>
                                <span className="text-green-600">
                                  {(indent.quantity_required || 0) - (indent.quantity_ordered || 0) - (indent.quantity_pending || 0)} {indent.unit}
                                </span>
                                <p className="text-xs text-muted-foreground">
                                  of {indent.quantity_required} remaining
                                </p>
                              </div>
                            ) : (
                              <span>{indent.quantity_required} {indent.unit}</span>
                            )}
                          </TableCell>
                          <TableCell>
                            {indent.status === 'rejected' ? (
                              <div>
                                <p className="text-sm text-muted-foreground" title={indent.raised_by_name}>
                                  {indent.raised_by_name || '-'}
                                </p>
                                <Badge className={`${statusColors.rejected} mt-1`}>Rejected</Badge>
                              </div>
                            ) : (
                              <p className="text-sm text-muted-foreground" title={indent.raised_by_name}>
                                {indent.raised_by_name || '-'}
                              </p>
                            )}
                          </TableCell>
                          <TableCell>
                            {/* Supplier selection available for all pending/partially_ordered indents */}
                            {(indent.status === 'pending' || indent.status === 'partially_ordered') ? (
                              <div className="flex gap-1 items-center">
                                <SearchableSelect
                                  options={[...suppliers]
                                    .sort((a, b) => a.name.localeCompare(b.name))
                                    .map(sup => ({
                                      value: sup.id,
                                      label: sup.name
                                    }))}
                                  value={indent.selected_supplier_id || ''}
                                  onValueChange={(val) => handleSupplierChange(indent.id, val)}
                                  placeholder="Select supplier"
                                  searchPlaceholder="Search suppliers..."
                                  emptyText="No suppliers found"
                                  className="w-44"
                                />
                                {indent.selected_supplier_id && (
                                  <Button
                                    type="button"
                                    variant="ghost"
                                    size="icon"
                                    className="h-8 w-8 shrink-0"
                                    onClick={() => handleSupplierChange(indent.id, '')}
                                    title="Clear supplier"
                                  >
                                    <X className="h-4 w-4 text-muted-foreground" />
                                  </Button>
                                )}
                              </div>
                            ) : indent.status === 'rejected' ? (
                              <span className="text-sm text-red-500">{indent.selected_supplier_name || '-'}</span>
                            ) : (
                              <span className="text-sm text-muted-foreground">{indent.selected_supplier_name || '-'}</span>
                            )}
                          </TableCell>
                          <TableCell className="text-right">
                            <div className="flex justify-end gap-1">
                              {indent.status === 'rejected' && (
                                <Button size="sm" variant="outline" onClick={() => handleResubmitIndent(indent.id)}>
                                  <RotateCcw className="w-4 h-4 mr-1" />
                                  Resubmit
                                </Button>
                              )}
                              {/* Note: Create WO button removed - indents should only go for purchase */}
                              {(indent.status === 'pending' || indent.status === 'rejected') && (
                                <>
                                  <Button size="sm" variant="ghost" onClick={() => openEditIndent(indent)} title="Edit indent">
                                    <Pencil className="w-4 h-4 text-blue-600" />
                                  </Button>
                                  <Button size="sm" variant="ghost" onClick={() => handleDeleteIndent(indent.id)} title="Delete indent">
                                    <Trash2 className="w-4 h-4 text-destructive" />
                                  </Button>
                                </>
                              )}
                            </div>
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
                
                {/* Send for PO Approval - Sticky action bar */}
                {canCreatePO && (
                  <div className="sticky bottom-0 mt-4 p-3 bg-primary/5 border border-primary/20 rounded-lg flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <Badge className="bg-primary text-white">
                        {selectedIndentList.length} items selected
                      </Badge>
                      <span className="text-sm text-muted-foreground">
                        from {new Set(selectedIndentList.map(i => i.selected_supplier_id)).size} supplier(s)
                      </span>
                    </div>
                    <Button onClick={() => {
                      initializePOLineItems();
                      setOpenPO(true);
                    }}>
                      <ShoppingCart className="w-4 h-4 mr-2" />
                      Send for PO Approval
                    </Button>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Awaiting PO Approval Tab */}
          <TabsContent value="awaiting">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Clock className="w-5 h-5 text-orange-600" />
                  Awaiting PO Approval
                </CardTitle>
              </CardHeader>
              <CardContent>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Indent #</TableHead>
                      <TableHead>Linked WO</TableHead>
                      <TableHead>Linked SO(s)</TableHead>
                      <TableHead>Item</TableHead>
                      <TableHead className="text-center">Location</TableHead>
                      <TableHead className="text-center">Source</TableHead>
                      <TableHead className="text-right">Qty</TableHead>
                      <TableHead>PO Number</TableHead>
                      <TableHead>Supplier</TableHead>
                      <TableHead>Date</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {awaitingApprovalIndents.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={10} className="text-center py-12">
                          <p className="text-muted-foreground">No indents awaiting PO approval</p>
                        </TableCell>
                      </TableRow>
                    ) : (
                      awaitingApprovalIndents.map((indent) => (
                        <TableRow key={indent.id}>
                          <TableCell>
                            <Badge variant="outline" className="font-mono">{indent.indent_number}</Badge>
                          </TableCell>
                          <TableCell>
                            {indent.triggered_by_wo_number ? (
                              <Badge variant="secondary" className="font-mono text-xs">{indent.triggered_by_wo_number}</Badge>
                            ) : (
                              <span className="text-muted-foreground text-sm">-</span>
                            )}
                          </TableCell>
                          <TableCell>
                            {(indent.so_numbers && indent.so_numbers.length > 0) ? (
                              <div className="flex flex-wrap gap-1">
                                {indent.so_numbers.slice(0, 3).map((so, i) => (
                                  <Badge key={i} variant="outline" className="text-xs">{so}</Badge>
                                ))}
                                {indent.so_numbers.length > 3 && (
                                  <Badge variant="outline" className="text-xs">+{indent.so_numbers.length - 3}</Badge>
                                )}
                              </div>
                            ) : indent.so_number ? (
                              <Badge variant="outline" className="text-xs">{indent.so_number}</Badge>
                            ) : (
                              <span className="text-muted-foreground text-xs">-</span>
                            )}
                          </TableCell>
                          <TableCell>
                            <p className="font-medium">{indent.item_name}</p>
                          </TableCell>
                          <TableCell className="text-center">
                            {getLocationDisplay(indent) ? (
                              <Badge variant="outline" className="text-xs">{getLocationDisplay(indent)}</Badge>
                            ) : (
                              <span className="text-muted-foreground text-xs">-</span>
                            )}
                          </TableCell>
                          <TableCell className="text-center">
                            <div className="flex items-center justify-center gap-1">
                              {sourceIcons[indent.source_type]}
                              <span className="text-sm">{indent.source_type}</span>
                            </div>
                          </TableCell>
                          <TableCell className="text-right font-medium">
                            {indent.quantity_required} {indent.unit}
                          </TableCell>
                          <TableCell>
                            <Badge className={statusColors.awaiting_approval}>
                              {indent.po_number}
                            </Badge>
                          </TableCell>
                          <TableCell className="text-sm">{indent.selected_supplier_name || '-'}</TableCell>
                          <TableCell className="text-muted-foreground text-sm">
                            {format(new Date(indent.created_at), 'dd/MM/yyyy')}
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Processed Indents Tab */}
          <TabsContent value="processed">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <CheckCircle2 className="w-5 h-5" />
                  Processed Indents
                </CardTitle>
              </CardHeader>
              <CardContent>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Indent #</TableHead>
                      <TableHead>Linked WO</TableHead>
                      <TableHead>Linked SO(s)</TableHead>
                      <TableHead>Item</TableHead>
                      <TableHead className="text-center">Location</TableHead>
                      <TableHead className="text-center">Source</TableHead>
                      <TableHead className="text-right">Qty</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead>PO/WO Number</TableHead>
                      <TableHead>Date</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {processedIndents.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={10} className="text-center py-12">
                          <p className="text-muted-foreground">No processed indents yet</p>
                        </TableCell>
                      </TableRow>
                    ) : (
                      processedIndents.map((indent) => (
                        <TableRow key={indent.id}>
                          <TableCell>
                            <Badge variant="outline" className="font-mono">{indent.indent_number}</Badge>
                          </TableCell>
                          <TableCell>
                            {indent.triggered_by_wo_number ? (
                              <Badge variant="secondary" className="font-mono text-xs">{indent.triggered_by_wo_number}</Badge>
                            ) : (
                              <span className="text-muted-foreground text-sm">-</span>
                            )}
                          </TableCell>
                          <TableCell>
                            {(indent.so_numbers && indent.so_numbers.length > 0) ? (
                              <div className="flex flex-wrap gap-1">
                                {indent.so_numbers.slice(0, 3).map((so, i) => (
                                  <Badge key={i} variant="outline" className="text-xs">{so}</Badge>
                                ))}
                                {indent.so_numbers.length > 3 && (
                                  <Badge variant="outline" className="text-xs">+{indent.so_numbers.length - 3}</Badge>
                                )}
                              </div>
                            ) : indent.so_number ? (
                              <Badge variant="outline" className="text-xs">{indent.so_number}</Badge>
                            ) : (
                              <span className="text-muted-foreground text-xs">-</span>
                            )}
                          </TableCell>
                          <TableCell>{indent.item_name}</TableCell>
                          <TableCell className="text-center">
                            {getLocationDisplay(indent) ? (
                              <Badge variant="outline" className="text-xs">{getLocationDisplay(indent)}</Badge>
                            ) : (
                              <span className="text-muted-foreground text-xs">-</span>
                            )}
                          </TableCell>
                          <TableCell className="text-center">
                            <div className="flex items-center justify-center gap-1">
                              {sourceIcons[indent.source_type]}
                              <span className="text-sm">{indent.source_type}</span>
                            </div>
                          </TableCell>
                          <TableCell className="text-right font-medium">
                            {indent.quantity_required} {indent.unit}
                          </TableCell>
                          <TableCell>
                            <Badge className={statusColors[indent.status]}>
                              {statusLabels[indent.status] || indent.status}
                            </Badge>
                          </TableCell>
                          <TableCell>
                            <Badge variant="outline" className="font-mono">
                              {indent.po_number || indent.wo_number}
                            </Badge>
                          </TableCell>
                          <TableCell className="text-muted-foreground text-sm">
                            {format(new Date(indent.created_at), 'dd/MM/yyyy')}
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>

        {/* Create PO Dialog */}
        <Dialog open={openPO} onOpenChange={setOpenPO}>
          <DialogContent className="max-w-6xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>Send PO for Approval</DialogTitle>
              <DialogDescription>
                Create a PO for {selectedIndentList.length} selected indent(s). It will be sent to CEO/Director for approval.
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4 py-4">
              {/* Supplier and Date Fields */}
              <div className="grid grid-cols-12 gap-4">
                <div className="space-y-2 col-span-3">
                  <Label>Supplier *</Label>
                  <div className="flex gap-2">
                    <div className="flex-1 min-w-0">
                      <SearchableSelect
                        options={[...suppliers]
                          .sort((a, b) => a.name.localeCompare(b.name))
                          .map(sup => ({
                            value: sup.id,
                            label: sup.name
                          }))}
                        value={poForm.supplier_id}
                        onValueChange={(val) => setPoForm({ ...poForm, supplier_id: val })}
                        placeholder="Select supplier"
                        searchPlaceholder="Type to search suppliers..."
                        emptyText="No suppliers found"
                      />
                    </div>
                    {poForm.supplier_id && (
                      <Button
                        type="button"
                        variant="outline"
                        size="icon"
                        className="shrink-0 h-9 w-9"
                        onClick={() => setPoForm({ ...poForm, supplier_id: '' })}
                        title="Clear supplier"
                      >
                        <X className="h-4 w-4" />
                      </Button>
                    )}
                  </div>
                </div>
                <div className="space-y-2 col-span-2">
                  <Label>Ship To (Unit) *</Label>
                  <Select
                    value={poForm.shipping_unit}
                    onValueChange={(val) => setPoForm({ ...poForm, shipping_unit: val })}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select unit" />
                    </SelectTrigger>
                    <SelectContent>
                      {unitAddresses.map(unit => (
                        <SelectItem key={unit.name} value={unit.name}>
                          {unit.name} - {unit.city}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2 col-span-2">
                  <Label>PO Date *</Label>
                  <Input
                    type="date"
                    value={poForm.po_date}
                    onChange={(e) => setPoForm({ ...poForm, po_date: e.target.value })}
                  />
                </div>
                <div className="space-y-2 col-span-2">
                  <Label>Expected Delivery</Label>
                  <Input
                    type="date"
                    value={poForm.expected_delivery_date}
                    onChange={(e) => setPoForm({ ...poForm, expected_delivery_date: e.target.value })}
                  />
                </div>
                <div className="space-y-2 col-span-3">
                  <Label>Notes</Label>
                  <Input
                    value={poForm.notes}
                    onChange={(e) => setPoForm({ ...poForm, notes: e.target.value })}
                    placeholder="Optional"
                  />
                </div>
              </div>

              {/* Line Items Table - Single Row Layout with Wider Dialog */}
              <div className="border rounded-lg overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow className="bg-muted/50">
                      <TableHead className="min-w-[200px]">Item</TableHead>
                      <TableHead className="w-32 text-center">Desired PO Date</TableHead>
                      <TableHead className="min-w-[180px]">Supplier Item Name</TableHead>
                      <TableHead className="w-28 text-center">PO Qty *</TableHead>
                      <TableHead className="w-24 text-center">UOM</TableHead>
                      <TableHead className="w-28 text-center">Supplier Qty *</TableHead>
                      <TableHead className="w-28">Supplier UOM *</TableHead>
                      <TableHead className="w-28">Rate *</TableHead>
                      <TableHead className="w-28 text-right">Amount</TableHead>
                      <TableHead className="w-12 text-center">History</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {poForm.lineItems.map((item, idx) => (
                      <TableRow key={item.indent_id}>
                        {/* Item with Indent reference */}
                        <TableCell>
                          <div className="font-semibold text-primary">{item.item_name}</div>
                          <div className="text-xs text-muted-foreground">
                            Indent: {item.indent_quantity} {item.indent_unit}
                            {item.available_qty !== item.indent_quantity && (
                              <span className="text-orange-600 ml-1">({item.available_qty} avail.)</span>
                            )}
                          </div>
                          {item.so_numbers?.length > 0 && (
                            <div className="flex flex-wrap gap-1 mt-1">
                              {item.so_numbers.filter(Boolean).map(s => (
                                <Badge key={s} variant="outline" className="text-xs bg-blue-50 text-blue-700 border-blue-200">SO: {s}</Badge>
                              ))}
                            </div>
                          )}
                        </TableCell>
                        {/* Desired PO Date (read-only context from indent) */}
                        <TableCell className="text-center">
                          <DesiredPoDateCell value={item.desired_po_date} />
                        </TableCell>
                        {/* Supplier Item Name */}
                        <TableCell>
                          <Input
                            value={item.supplier_item_name || ''}
                            onChange={(e) => updateLineItem(idx, 'supplier_item_name', e.target.value)}
                            className="h-9"
                            placeholder="Generic name"
                          />
                        </TableCell>
                        {/* PO Qty - Auto-filled with available qty, editable */}
                        <TableCell>
                          <Input
                            type="number"
                            value={item.po_qty}
                            onChange={(e) => updateLineItem(idx, 'po_qty', e.target.value)}
                            className="h-9 w-24 text-center"
                            min="0"
                            max={item.available_qty || item.indent_quantity}
                            step="0.01"
                            title={`Max: ${item.available_qty || item.indent_quantity} ${item.indent_unit}`}
                          />
                        </TableCell>
                        {/* System UOM - Read Only */}
                        <TableCell className="text-center">
                          <span className="px-2 py-1 bg-gray-100 rounded text-sm font-medium">
                            {item.indent_unit}
                          </span>
                        </TableCell>
                        {/* Supplier Qty */}
                        <TableCell>
                          <Input
                            type="number"
                            value={item.quantity}
                            onChange={(e) => updateLineItem(idx, 'quantity', e.target.value)}
                            className="h-9 w-24 text-center"
                            min="0"
                            step="0.01"
                          />
                        </TableCell>
                        {/* Supplier UOM */}
                        <TableCell>
                          <Select
                            value={item.unit}
                            onValueChange={(val) => updateLineItem(idx, 'unit', val)}
                          >
                            <SelectTrigger className="h-9 w-24">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              {uomOptions.map(uom => (
                                <SelectItem key={uom} value={uom}>{uom}</SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </TableCell>
                        {/* Price Rate */}
                        <TableCell>
                          <Input
                            type="number"
                            value={item.unit_price}
                            onChange={(e) => updateLineItem(idx, 'unit_price', e.target.value)}
                            className="h-9 w-24"
                            min="0"
                            step="0.01"
                            placeholder="₹"
                          />
                        </TableCell>
                        {/* Amount */}
                        <TableCell className="text-right font-medium">
                          {formatCurrency(item.amount || 0)}
                        </TableCell>
                        {/* History Button */}
                        <TableCell className="text-center">
                          <Button
                            type="button"
                            variant="ghost"
                            size="sm"
                            className="h-8 w-8 p-0"
                            onClick={() => {
                              setPriceHistoryItem({
                                itemId: item.item_id,
                                itemName: item.item_name,
                                supplierId: poForm.supplier_id,
                                supplierName: suppliers.find(s => s.id === poForm.supplier_id)?.name,
                                currentPrice: parseFloat(item.unit_price) || 0,
                                unit: item.unit
                              });
                              setPriceHistoryOpen(true);
                            }}
                            title="View Price History"
                          >
                            <History className="h-4 w-4 text-blue-600" />
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>

              {/* Totals Section - Matching PO View Dialog Layout */}
              <div className="border rounded-lg p-4 bg-muted/30">
                <div className="grid grid-cols-2 gap-4">
                  {/* Left column - Breakdown with inputs */}
                  <div className="space-y-2">
                    <div className="flex justify-between text-sm">
                      <span>Sub Total:</span>
                      <span className="font-medium">{formatCurrency(calculateTotals().subTotal)}</span>
                    </div>
                    <div className="flex justify-between items-center text-sm">
                      <span>Other Charges (Taxable):</span>
                      <Input
                        type="number"
                        value={poForm.other_charges_taxable}
                        onChange={(e) => setPoForm({ ...poForm, other_charges_taxable: e.target.value })}
                        className="w-28 h-7 text-right text-sm"
                        min="0"
                        step="0.01"
                        placeholder="₹0.00"
                      />
                    </div>
                    <div className="flex justify-between items-center text-sm">
                      <span>GST Rate:</span>
                      <div className="flex items-center gap-1">
                        <Input
                          type="number"
                          value={poForm.gst_rate}
                          onChange={(e) => setPoForm({ ...poForm, gst_rate: e.target.value })}
                          className="w-20 h-7 text-right text-sm"
                          min="0"
                          max="100"
                          step="0.01"
                        />
                        <span className="text-muted-foreground">%</span>
                      </div>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span>GST Amount:</span>
                      <span className="font-medium">{formatCurrency(calculateTotals().gstAmount)}</span>
                    </div>
                    <div className="flex justify-between items-center text-sm">
                      <span>Other Charges (Non-Taxable):</span>
                      <Input
                        type="number"
                        value={poForm.other_charges_non_taxable}
                        onChange={(e) => setPoForm({ ...poForm, other_charges_non_taxable: e.target.value })}
                        className="w-28 h-7 text-right text-sm"
                        min="0"
                        step="0.01"
                        placeholder="₹0.00"
                      />
                    </div>
                  </div>
                  
                  {/* Right column - Grand Total */}
                  <div className="flex items-center justify-end border-l pl-4">
                    <div className="text-right">
                      <p className="text-sm text-muted-foreground">Grand Total</p>
                      <p className="text-3xl font-bold text-primary">
                        {formatCurrency(calculateTotals().grandTotal)}
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Advance Payment Request Section */}
              <div className="border rounded-lg p-4 bg-amber-50/50">
                <div className="flex items-center gap-3 mb-3">
                  <input
                    type="checkbox"
                    id="advance_requested"
                    checked={poForm.advance_requested}
                    onChange={(e) => setPoForm({ ...poForm, advance_requested: e.target.checked })}
                    className="w-4 h-4 rounded border-gray-300"
                  />
                  <Label htmlFor="advance_requested" className="font-medium cursor-pointer">
                    Request Advance Payment
                  </Label>
                </div>
                
                {poForm.advance_requested && (
                  <div className="grid grid-cols-2 gap-4 pt-2 border-t border-amber-200">
                    <div className="space-y-2">
                      <Label>Advance Amount (₹) *</Label>
                      <Input
                        type="number"
                        value={poForm.advance_amount}
                        onChange={(e) => setPoForm({ ...poForm, advance_amount: e.target.value })}
                        placeholder="Enter amount"
                        min="0"
                        step="0.01"
                        className="bg-white"
                      />
                      {poForm.advance_amount && calculateTotals().grandTotal > 0 && (
                        <p className="text-xs text-muted-foreground">
                          {((parseFloat(poForm.advance_amount) / calculateTotals().grandTotal) * 100).toFixed(1)}% of PO value
                        </p>
                      )}
                    </div>
                    <div className="space-y-2">
                      <Label>Advance Payment Due Date *</Label>
                      <Input
                        type="date"
                        value={poForm.advance_payment_due_date}
                        onChange={(e) => setPoForm({ ...poForm, advance_payment_due_date: e.target.value })}
                        className="bg-white"
                      />
                    </div>
                  </div>
                )}
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setOpenPO(false)}>Cancel</Button>
              <Button onClick={handleCreatePO}>Send for Approval</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Edit Indent Dialog */}
        <Dialog open={!!editingIndent} onOpenChange={(open) => !open && setEditingIndent(null)}>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Pencil className="w-5 h-5 text-blue-600" />
                Edit Indent
              </DialogTitle>
              <DialogDescription>
                {editingIndent?.indent_number} - {editingIndent?.item_name}
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Quantity Required</Label>
                  <Input
                    type="number"
                    min="0"
                    step="0.01"
                    value={editForm.quantity_required}
                    onChange={(e) => setEditForm({ ...editForm, quantity_required: e.target.value })}
                  />
                </div>
                <div className="space-y-2">
                  <Label>Unit (from Item Master)</Label>
                  {/* UOM is read-only - comes from Item Master */}
                  <Input
                    value={editForm.unit}
                    disabled
                    className="bg-muted cursor-not-allowed"
                    title="UOM is determined by Item Master and cannot be changed"
                  />
                </div>
              </div>
              <div className="space-y-2">
                <Label>Desired PO Date (set by requester)</Label>
                {/* Read-only — owned by the user who raised the indent, not the PM. */}
                <Input
                  value={formatDesiredPoDate(editingIndent?.desired_po_date)}
                  disabled
                  className="bg-muted cursor-not-allowed"
                  title="Desired PO Date is set at indent creation and cannot be changed here"
                  data-testid="edit-indent-desired-po-date-readonly"
                />
              </div>
              {editingIndent?.status === 'pending' && (
                <div className="space-y-2">
                  <Label>Link to SO</Label>
                  <Select
                    value={editForm.sales_order_id || 'none'}
                    onValueChange={(value) => {
                      if (value === 'none') {
                        setEditForm({ ...editForm, sales_order_id: '', so_number: '' });
                      } else {
                        const so = editSalesOrders.find(s => s.id === value);
                        setEditForm({ ...editForm, sales_order_id: value, so_number: so?.so_number || '' });
                      }
                    }}
                  >
                    <SelectTrigger data-testid="edit-indent-so-select">
                      <SelectValue placeholder="Select SO..." />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">None</SelectItem>
                      {editSalesOrders.map(so => (
                        <SelectItem key={so.id} value={so.id}>{so.so_number} — {so.customer_name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}
              <div className="space-y-2">
                <Label>Remarks</Label>
                <Textarea
                  value={editForm.remarks}
                  onChange={(e) => setEditForm({ ...editForm, remarks: e.target.value })}
                  placeholder="Add any remarks..."
                  rows={3}
                />
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setEditingIndent(null)}>Cancel</Button>
              <Button onClick={handleUpdateIndent}>Save Changes</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Price History Popup */}
        <PriceHistoryPopup
          open={priceHistoryOpen}
          onClose={() => {
            setPriceHistoryOpen(false);
            setPriceHistoryItem(null);
          }}
          itemId={priceHistoryItem?.itemId}
          itemName={priceHistoryItem?.itemName}
          supplierId={priceHistoryItem?.supplierId}
          supplierName={priceHistoryItem?.supplierName}
          currentPrice={priceHistoryItem?.currentPrice}
          unit={priceHistoryItem?.unit}
        />
      </div>
    </DashboardLayout>
  );
}
